import React, { Fragment, useEffect, useState } from "react";
import { CardBody, Col, Row, Table } from "reactstrap";
import { Link } from "react-router-dom";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender
} from '@tanstack/react-table';

// We keep DebouncedInput for the global search functionality
const DebouncedInput = ({
                            value: initialValue,
                            onChange,
                            debounce = 500,
                            ...props
                        }) => {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value);
        }, debounce);

        return () => clearTimeout(timeout);
    }, [debounce, onChange, value]);

    return (
        <input
            {...props}
            value={value}
            id="search-bar-0"
            className="form-control search"
            onChange={(e) => setValue(e.target.value)}
        />
    );
};

const TableContainer = ({
                            columns,
                            data,
                            enableGlobalFilter = false,
                            customPageSize,
                            tableClass,
                            theadClass,
                            trClass,
                            thClass,
                            divClass,
                            SearchPlaceholder,
                            handleRowClick,
                            customTableRowProps,
                            showPagination = true,
                            manualPagination = false,
                            pageCount = -1,
                            pageIndex: controlledPageIndex = 0,
                            totalCount,
                            onPageChange,
                        }) => {
    const [globalFilter, setGlobalFilter] = useState('');

    const table = useReactTable({
        columns,
        data,
        state: {
            globalFilter,
            ...(manualPagination
                ? { pagination: { pageIndex: controlledPageIndex, pageSize: data.length || 1 } }
                : {}),
        },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        ...(manualPagination
            ? { manualPagination: true, pageCount }
            : { getPaginationRowModel: getPaginationRowModel() }),
    });

    const {
        getHeaderGroups,
        getRowModel,
        getCanPreviousPage,
        getCanNextPage,
        getPageOptions,
        setPageIndex,
        nextPage,
        previousPage,
        setPageSize,
        getState
    } = table;

    useEffect(() => {
        if (customPageSize && !manualPagination) {
            setPageSize(customPageSize);
        }
    }, [customPageSize, manualPagination, setPageSize]);

    const currentPageIndex = manualPagination ? controlledPageIndex : getState().pagination.pageIndex;
    const canPreviousPage = manualPagination ? currentPageIndex > 0 : getCanPreviousPage();
    const canNextPage = manualPagination ? currentPageIndex < pageCount - 1 : getCanNextPage();
    const pageOptions = manualPagination
        ? Array.from({ length: Math.max(pageCount, 0) }, (_, i) => i)
        : getPageOptions();
    const resultsTotal = manualPagination ? (totalCount ?? data.length) : data.length;
    const currentPageSize = manualPagination
        ? data.length
        : Math.min(getState().pagination.pageSize, data.length);

    const goToPage = (index) => {
        if (manualPagination) {
            onPageChange && onPageChange(index);
        } else {
            setPageIndex(index);
        }
    };
    const goToNextPage = () => {
        if (manualPagination) {
            onPageChange && onPageChange(currentPageIndex + 1);
        } else {
            nextPage();
        }
    };
    const goToPreviousPage = () => {
        if (manualPagination) {
            onPageChange && onPageChange(currentPageIndex - 1);
        } else {
            previousPage();
        }
    };

    return (
        <Fragment>
            {/* Only show global filter if enabled */}
            {enableGlobalFilter && (
                <Row className="mb-3">
                    <CardBody className="border border-dashed border-end-0 border-start-0">
                        <Row>
                            <Col sm={5}>
                                <div className="search-box me-2 mb-2 d-inline-block col-12">
                                    <DebouncedInput
                                        value={globalFilter ?? ''}
                                        onChange={value => setGlobalFilter(value)}
                                        placeholder={SearchPlaceholder}
                                    />
                                    <i className="bx bx-search-alt search-icon"></i>
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Row>
            )}

            <div className={divClass}>
                <Table hover className={tableClass}>
                    <thead className={theadClass}>
                    {getHeaderGroups().map((headerGroup) => (
                        <tr className={trClass} key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <th
                                    key={header.id}
                                    className={thClass}
                                    onClick={header.column.getToggleSortingHandler()}
                                >
                                    {header.isPlaceholder ? null : (
                                        <React.Fragment>
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                            {{
                                                asc: ' ↑',
                                                desc: ' ↓',
                                            }[header.column.getIsSorted()] ?? null}
                                        </React.Fragment>
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>

                    <tbody>
                    {getRowModel().rows.map((row) => {
                        // Determine row props - either from custom function or default
                        const rowProps = customTableRowProps ? customTableRowProps(row) : {};

                        return (
                            <tr
                                key={row.id}
                                onClick={() => handleRowClick && handleRowClick(row)}
                                {...rowProps}
                                style={{
                                    backgroundColor: row.original.floor % 2 === 1 ? '#e6e6e6' : '#ffffff',
                                    ...(rowProps.style || {})
                                }}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id}>
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                        )}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                    </tbody>
                </Table>
            </div>

            {showPagination && (
                <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                    <div className="col-sm">
                        <div className="text-muted">
                            Showing <span className="fw-semibold ms-1">{currentPageSize}</span> of{" "}
                            <span className="fw-semibold">{resultsTotal}</span> Results
                        </div>
                    </div>
                    <div className="col-sm-auto">
                        <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
                            <li className={!canPreviousPage ? "page-item disabled" : "page-item"}>
                                <Link to="#" className="page-link" onClick={goToPreviousPage}>
                                    Previous
                                </Link>
                            </li>
                            {pageOptions.map((item, key) => (
                                <React.Fragment key={key}>
                                    <li className="page-item">
                                        <Link
                                            to="#"
                                            className={
                                                currentPageIndex === item ? "page-link active" : "page-link"
                                            }
                                            onClick={() => goToPage(item)}
                                        >
                                            {item + 1}
                                        </Link>
                                    </li>
                                </React.Fragment>
                            ))}
                            <li className={!canNextPage ? "page-item disabled" : "page-item"}>
                                <Link to="#" className="page-link" onClick={goToNextPage}>
                                    Next
                                </Link>
                            </li>
                        </ul>
                    </div>
                </Row>
            )}

        </Fragment>
    );
};

export default TableContainer;
